import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TranslationService } from './translation.service';

type TranslationUrlResponse = Array<{ path?: string } | string>;

@Injectable({
  providedIn: 'root',
})
export class PublicUrlTranslationService {
  private readonly publicBaseUrl = String(environment.cncsCoreUrl || '').replace(/\/+$/, '');
  private readonly translatedUrlCache = new Map<string, Map<string, string>>();
  private readonly languageAliases = new Set(['en', 'fr', 'mfe', 'kr', 'mu']);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly translation: TranslationService,
  ) {}

  get baseUrl(): string {
    return this.publicBaseUrl;
  }

  normalizeLanguage(lang?: string | null): string {
    const normalized = String(
      lang ?? this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang() ?? 'fr',
    )
      .trim()
      .toLowerCase();

    if (normalized === 'kr' || normalized === 'mu' || normalized === 'mfe') {
      return 'mfe';
    }

    return normalized.slice(0, 2) || 'fr';
  }

  normalizePath(path: string): string {
    const normalizedPath = this.normalizeAbsolutePath(path);
    const segments = normalizedPath.split('/').filter(Boolean);

    if (segments.length > 0 && this.isLanguageSegment(segments[0])) {
      const withoutLanguage = segments.slice(1).join('/');
      return withoutLanguage ? `/${withoutLanguage}` : '/';
    }

    return normalizedPath;
  }

  normalizeResolvedPath(path: string): string {
    return this.normalizeAbsolutePath(path);
  }

  private normalizeAbsolutePath(path: string): string {
    const rawValue = String(path || '').trim();
    if (!rawValue) {
      return '/';
    }

    try {
      const parsedUrl = new URL(rawValue, this.publicBaseUrl || 'http://localhost');
      const normalizedPath = parsedUrl.pathname || '/';
      return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
    } catch {
      return rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
    }
  }

  buildFallbackUrl(path: string, lang?: string | null): string {
    const normalizedPath = this.normalizePath(path);
    const normalizedLang = this.normalizeLanguage(lang);
    const localizedPath = normalizedPath === '/' ? `/${normalizedLang}` : `/${normalizedLang}${normalizedPath}`;

    return this.publicBaseUrl ? `${this.publicBaseUrl}${localizedPath}` : localizedPath;
  }

  getTranslatedPublicUrls(paths: string[], lang?: string | null): Observable<Record<string, string>> {
    const normalizedLang = this.normalizeLanguage(lang);
    const normalizedPaths = Array.from(new Set(paths.map((path) => this.normalizePath(path))));

    if (normalizedPaths.length === 0) {
      return of({});
    }

    const cache = this.getCacheForLanguage(normalizedLang);
    const missingPaths = normalizedPaths.filter((path) => !cache.has(path));

    if (missingPaths.length === 0 || !this.publicBaseUrl) {
      return of(this.buildUrlRecord(normalizedPaths, normalizedLang));
    }

    const requestPaths = missingPaths.map((path) =>
      encodeURIComponent(this.buildLocalizedPath(path, normalizedLang)),
    );
    const params = new HttpParams().set('url', JSON.stringify(requestPaths));

    return this.httpClient
      .get<TranslationUrlResponse>(`${this.publicBaseUrl}/api/translation/${normalizedLang}/translateUrls`, { params })
      .pipe(
        map((response) => {
          missingPaths.forEach((path, index) => {
            cache.set(path, this.resolveTranslatedUrl(response?.[index], path, normalizedLang));
          });

          return this.buildUrlRecord(normalizedPaths, normalizedLang);
        }),
        catchError(() => of(this.buildUrlRecord(normalizedPaths, normalizedLang))),
      );
  }

  private getCacheForLanguage(lang: string): Map<string, string> {
    if (!this.translatedUrlCache.has(lang)) {
      this.translatedUrlCache.set(lang, new Map<string, string>());
    }

    return this.translatedUrlCache.get(lang)!;
  }

  private buildUrlRecord(paths: string[], lang: string): Record<string, string> {
    const cache = this.getCacheForLanguage(lang);

    return paths.reduce<Record<string, string>>((accumulator, path) => {
      accumulator[path] = cache.get(path) ?? this.buildFallbackUrl(path, lang);
      return accumulator;
    }, {});
  }

  private buildLocalizedPath(path: string, lang: string): string {
    const normalizedPath = this.normalizePath(path);
    return normalizedPath === '/' ? `/${lang}` : `/${lang}${normalizedPath}`;
  }

  private resolveTranslatedUrl(responseItem: TranslationUrlResponse[number], path: string, lang: string): string {
    const translatedPath = typeof responseItem === 'string' ? responseItem : responseItem?.path;

    if (
      typeof translatedPath !== 'string' ||
      !translatedPath.trim() ||
      translatedPath === '/not-found' ||
      translatedPath === '/undefined'
    ) {
      return this.buildFallbackUrl(path, lang);
    }

    if (/^https?:\/\//i.test(translatedPath)) {
      return translatedPath;
    }

    const normalizedPath = this.normalizeResolvedPath(translatedPath);
    return this.publicBaseUrl ? `${this.publicBaseUrl}${normalizedPath}` : normalizedPath;
  }

  private isLanguageSegment(segment: string): boolean {
    const normalized = this.normalizeLanguage(segment);
    if (this.languageAliases.has(normalized)) {
      return true;
    }

    const configuredLanguages = this.translation.translate.getLangs().map((lang) => this.normalizeLanguage(lang));
    return configuredLanguages.includes(normalized);
  }
}